import { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { useInstallPrompt } from "../hooks/useInstallPrompt";
import { trackEvent } from "../tracking/utils";
import { useEffectOnceWithDependencies } from "../hooks/useEffectOnceWithDependencies";
import { isPrerender } from "../lib/isPrerender";

const STORAGE_KEY = "install_prompt_dismissed";

const S = {
  Banner: styled.div`
    position: fixed;
    bottom: 16px;
    left: 50%;
    transform: translateX(-50%);
    background: #fff;
    color: #031b4e;
    border-radius: 10px;
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.18);
    padding: 12px 16px;
    display: flex;
    align-items: center;
    gap: 12px;
    z-index: 50;
    max-width: calc(100% - 32px);
    font-size: 14px;

    @media (max-width: 992px) {
      width: calc(100% - 48px);
    }
  `,
  Text: styled.span`
    flex: 1;
    line-height: 1.35;
  `,
  Install: styled.button`
    background: #0067ff;
    color: #fff;
    border: none;
    border-radius: 6px;
    padding: 8px 12px;
    font-weight: 600;
    font-size: 13px;
    cursor: pointer;
    white-space: nowrap;
  `,
  Dismiss: styled.button`
    background: transparent;
    color: #6a7180;
    border: none;
    padding: 6px 4px;
    font-size: 18px;
    line-height: 1;
    cursor: pointer;
  `,
};

export function InstallPromptBanner() {
  const { canInstall, promptInstall } = useInstallPrompt();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      setDismissed(localStorage.getItem(STORAGE_KEY) === "1");
    } catch {
      /* ignore */
    }
  }, []);

  const shouldShow = canInstall && !dismissed && !isPrerender();

  useEffectOnceWithDependencies(
    () => {
      if (shouldShow) trackEvent("Install Prompt Shown");
    },
    [shouldShow],
    () => shouldShow
  );

  if (!shouldShow) return null;

  const persistDismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  const handleInstall = async () => {
    const outcome = await promptInstall();
    trackEvent("Install Prompt Result", { outcome });
    if (outcome === "dismissed" || outcome === "unavailable") {
      persistDismiss();
      setDismissed(true);
    }
  };

  const handleDismiss = () => {
    trackEvent("Install Prompt Dismissed");
    persistDismiss();
    setDismissed(true);
  };

  return (
    <S.Banner role="dialog" aria-label="App installeren">
      <S.Text>
        Installeer <strong>Heb ik tegenwind?</strong> op je beginscherm voor snelle toegang tijdens het fietsen.
      </S.Text>
      <S.Install onClick={handleInstall} type="button">
        Installeren
      </S.Install>
      <S.Dismiss
        onClick={handleDismiss}
        type="button"
        aria-label="Sluit installatie-suggestie"
      >
        ×
      </S.Dismiss>
    </S.Banner>
  );
}
